import { BitmapLayer } from '@deck.gl/layers/typed';
import { Bbox } from 'ais-facade-api/api';
import { useLazyGetWavesQuery } from 'app/api/weather';
import { useAppSelector } from 'app/store/store';
import { createColoredMap, normalizeWeatherData, PaletteEnum, toEPSG3857 } from 'app/helpers/weather/dataProcessor';
import { useEffect, useState } from 'react';

export const useWavesLayer = (bbox: Bbox, viewState: Record<string, number>, isWeatherLayerHiddenMode: boolean) => {
  const [coloredImage, setColoredImage] = useState<ArrayBuffer | string | null>(null);
  const { width, height } = viewState;

  const [getWaves, { ...getWavesRequest }] = useLazyGetWavesQuery();

  const visible = useAppSelector((state) => state.weatherSlice.layersVisibility.waves) && !isWeatherLayerHiddenMode;
  const user = useAppSelector((state) => state.userSlice.user);

  const canvas = document.createElement('canvas') as HTMLCanvasElement;
  const ctx = canvas?.getContext('2d');


  useEffect(() => {
    canvas.width = viewState.width;
    canvas.height = viewState.height;
  }, []);

  const worldBbox: Bbox = {
    first: { latitude: -85, longitude: -180 },
    second: { latitude: 85, longitude: 180 },
    z: 3,
  };

  useEffect(() => {
    if (user && typeof bbox?.first?.latitude === 'number' && ctx && visible) {
      if (width && height && !coloredImage) {
        canvas.width = width;
        canvas.height = height;
        const convertedBbox = toEPSG3857(worldBbox);
        getWaves({
          bbox: convertedBbox,
          width,
          height,
        })
          .unwrap()
          .then((res: Blob) => {
            const imageUrl = URL.createObjectURL(res);
            const image = new Image();
            image.src = imageUrl;
            image.onload = () => {
              ctx.clearRect(0, 0, Number(width), Number(height));
              ctx.drawImage(image, 0, 0);
              const imageData = ctx.getImageData(0, 0, Number(width), Number(height));
              const data = imageData.data;

              const normalizedData = normalizeWeatherData(data);
              createColoredMap(viewState, normalizedData, PaletteEnum.Waves).then((blob) => {
                const reader = new FileReader();
                reader.readAsDataURL(blob as Blob);
                reader.onloadend = () => {
                  setColoredImage(reader.result);
                };
              });
            };
          });
      }
    } else {
      setColoredImage(null);
    }
  }, [user, visible, viewState]);

  const layer = coloredImage
    ? new BitmapLayer({
      id: 'layer-waves',
      image: coloredImage,
      bounds: [-180, -85, 180, 85],
      visible: !getWavesRequest.isFetching && visible,
      pickable: false,
    })
    : null;
  return layer;
};
